import {
  Box,
  Button,
  Grid,
  Paper,
  Switch,
  Typography,
  useMediaQuery,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { getUser } from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 2,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh'
  },
  mainPaper: {
    p: { xs: 2, md: 3 },
    borderRadius: 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  categoryTitle: {
    fontWeight: 'bold',
    mt: 4,
    mb: 2,
    color: '#60a5fa',
    textTransform: 'uppercase',
    borderBottom: '2px solid #60a5fa',
    display: 'inline-block',
  },
  tabs: {
    '& .MuiTab-root': {
      color: 'rgba(255, 255, 255, 0.6)',
      '&.Mui-selected': {
        color: '#fff'
      }
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#60a5fa'
    }
  },
  vendorCard: {
    p: 3,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 3,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.4)',
    },
    background: 'rgba(45, 45, 60, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(4px)',
  },
  vendorImage: {
    flexShrink: 0,
    width: { xs: '100%', md: '350px' },
    height: { xs: '180px', md: '200px' },
    borderRadius: 2,
    backgroundColor: 'rgba(50, 50, 70, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0px 2px 5px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  vendorName: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: '#fff',
    textAlign: 'center',
  },
  companyText: {
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  resetButton: {
    fontWeight: 'bold',
    px: 2,
    py: 1.5,
    borderRadius: 2,
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
  saveButton: {
    fontWeight: 'bold',
    px: 4,
    py: 1.5,
    borderRadius: 2,
    backgroundColor: '#60a5fa',
    color: '#fff',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    ':hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    },
  },
  alertError: {
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
    color: '#f87171',
    border: '1px solid rgba(220, 38, 38, 0.2)',
    '& .MuiAlert-icon': {
      color: '#f87171'
    }
  },
  alertSuccess: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#4ade80',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    '& .MuiAlert-icon': {
      color: '#4ade80'
    }
  },
  dialogPaper: {
    borderRadius: 2,
    maxWidth: '450px',
    width: '90%',
    backgroundColor: 'rgba(35, 35, 50, 0.95)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  warningBox: {
    p: 3,
    bgcolor: 'rgba(244, 67, 54, 0.1)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
    borderRadius: 2,
  },
  warningDialogTitle: {
    fontWeight: 'bold', 
    color: '#f87171'
  },
  warningDialogText: {
    color: 'rgba(255, 255, 255, 0.8)'
  },
  dialogButton: {
    color: '#fff'
  },
  switchStyles: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#4ade80', // 밝은 초록색
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#22c55e', // 약간 더 진한 초록색
    },
  },
  // 에이전트 드롭다운을 위한 추가 스타일
  formControl: {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        },
      },
    },
  },
  menuItemHighlight: {
    '&.MuiMenuItem-root': {
      color: '#ce93d8',
      fontWeight: 'bold',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(206, 147, 216, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(206, 147, 216, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(206, 147, 216, 0.3)',
        },
      },
    },
  },
};

// 회사 이름 매핑 상수 - 컴포넌트 외부로 이동
const companyMapping = {
  honorlink: '아너링크',
  thenuts: '더너츠',
  axegames: '엑스게임',
  rave: '레이브게임',
  hive_star: '하이브스타',
  the_a: '더에이',
  devaplay: '데바플레이',
  next_gate: '넥스트게이트', // next_gate 추가
};

const GameVendor = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const isMounted = useRef(true);

  // 상태 관리
  const [categories, setCategories] = useState([
    {
      name: '카지노',
      vendors: [],
    },
    {
      name: '슬롯',
      vendors: [],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [agentList, setAgentList] = useState([]); // 에이전트 목록
  const [selectedAgentId, setSelectedAgentId] = useState(''); // 선택된 에이전트 ID
  
  // 각 벤더별 API 이름(게임회사) 목록을 저장하는 상태 추가
  const [vendorApiNames, setVendorApiNames] = useState({});

  // 경고 다이얼로그 상태
  const [warningDialog, setWarningDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    title: '',
  });

  // 센터 경고 메시지 상태
  const [centerWarning, setCenterWarning] = useState({
    open: false,
    message: '',
  });

  // 메시지 초기화 함수
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    fetchVendors(categories[newValue].name === '카지노' ? 'casino' : 'slot');
  };

  // 컴포넌트 마운트/언마운트 처리
  useEffect(() => {
    // 컴포넌트 마운트 시 에이전트 목록과 데이터 로드
    fetchAgentList();
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 에이전트 선택 시 핸들러
  useEffect(() => {
    if (selectedAgentId) {
      fetchVendors(categories[activeTab].name === '카지노' ? 'casino' : 'slot');
    }
  }, [selectedAgentId]);

  // 벤더 목록이 로드된 후 각 벤더별 API 이름 가져오기
  useEffect(() => {
    const vendors = categories[activeTab]?.vendors || [];
    if (vendors.length > 0) {
      // 각 벤더별로 API 이름 가져오기
      vendors.forEach(vendor => {
        fetchApiNames(vendor.name);
      });
    }
  }, [categories[activeTab]?.vendors]);

  // 특정 제공자(provider)의 API 이름 목록 가져오기
  const fetchApiNames = async (provider) => {
    try {
      const response = await axiosInstance.get(`/games/api-names?provider=${encodeURIComponent(provider)}`);
      
      if (!isMounted.current) return;

      if (response.data && response.data.success) {
        // API 응답에서 api_names 배열 추출
        const apiNames = response.data.api_names.map(item => item.api_name);
        
        // 벤더별 API 이름 목록 업데이트
        setVendorApiNames(prev => ({
          ...prev,
          [provider]: apiNames
        }));
      }
    } catch (err) {
      console.error(`${provider}의 API 이름 가져오기 실패:`, err);
    }
  };

  // 에이전트 목록 가져오기
  const fetchAgentList = async () => {
    try {
      const response = await axiosInstance.get('/agent/children');
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        const agentTree = response.data.agent_tree;
        
        // 현재 로그인한 에이전트와 하위 에이전트들을 목록에 추가
        const agents = [
          { id: agentTree.id, name: agentTree.id, level: 0 }, // 현재 로그인한 에이전트
          ...agentTree.children.map(child => ({
            id: child.id,
            name: child.id,
            level: child.level,
          })),
        ];
        
        setAgentList(agents);
        
        // 현재 사용자 ID를 기본 선택 값으로 설정
        const user = getUser();
        if (user && user.id) {
          setSelectedAgentId(user.id);
        } else if (agents.length > 0) {
          setSelectedAgentId(agents[0].id);
        }
      }
    } catch (err) {
      console.error('에이전트 목록 가져오기 실패:', err);
      if (isMounted.current) {
        setError('에이전트 목록을 불러오는데 실패했습니다.');
      }
    }
  };

  // 벤더 데이터 가져오기
  const fetchVendors = async category => {
    setLoading(true);
    clearMessages();

    try {
      // 선택된 에이전트 ID 사용
      const agentId = selectedAgentId;
      
      if (!agentId) {
        const user = getUser();
        if (!user || !user.id) {
          setError('로그인 정보를 찾을 수 없습니다.');
          setLoading(false);
          return;
        }
      }

      const response = await axiosInstance.get(`/games/agent/company?agent_id=${agentId || ''}&category=${category}`);

      if (!isMounted.current) return;

      if (response.data && response.data.success) {
        const vendorsData = response.data.vendors;
        console.log('벤더 데이터:', vendorsData);

        // 현재 카테고리 인덱스 결정
        const categoryIndex = category === 'casino' ? 0 : 1;

        // 벤더 데이터 처리
        const vendorsList = [];

        // 각 공급자별로 데이터 처리
        Object.keys(vendorsData).forEach(providerName => {
          const providerData = vendorsData[providerName][0]; // 배열의 첫 번째 항목

          vendorsList.push({
            name: providerName,
            honorlink: providerData.game_company === 'honorlink',
            thenuts: providerData.game_company === 'thenuts',
            axegames: providerData.game_company === 'axegames',
            rave: providerData.game_company === 'rave',
            hive_star: providerData.game_company === 'hive_star',
            the_a: providerData.game_company === 'the_a',
            devaplay: providerData.game_company === 'devaplay',
            next_gate: providerData.game_company === 'next_gate', // next_gate 추가
            use: providerData.use === 1, // 사용 여부
            id: providerData.id, // 벤더 ID
            number: providerData.number, // 정렬 순서
          });
        });

        // 정렬 순서대로 정렬
        vendorsList.sort((a, b) => a.number - b.number);

        // 카테고리 업데이트
        setCategories(prev => {
          const updated = [...prev];
          updated[categoryIndex] = {
            ...updated[categoryIndex],
            vendors: vendorsList,
          };
          return updated;
        });
        
        // 벤더 목록이 업데이트되면 API 이름 목록 초기화
        setVendorApiNames({});
      } else {
        throw new Error('유효한 데이터가 반환되지 않았습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;

      console.error('게임사 목록 가져오기 실패:', err);
      setError('게임사 목록을 불러오는데 실패했습니다.');
      setSuccess(null);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 스위치 변경 핸들러
  const handleSwitchChange = (categoryIndex, vendorIndex, key) => {
    // 다른 모든 회사의 스위치를 끄고 선택한 것만 켜기
    const allCompanyKeys = ['honorlink', 'thenuts', 'axegames', 'rave', 'hive_star', 'the_a', 'devaplay', 'next_gate']; // next_gate 추가

    setCategories(prev =>
      prev.map((category, cIndex) =>
        cIndex === categoryIndex
          ? {
              ...category,
              vendors: category.vendors.map((vendor, vIndex) =>
                vIndex === vendorIndex
                  ? {
                      ...vendor,
                      // 모든 회사 스위치를 false로 설정하고 선택한 회사만 true로 설정
                      ...Object.fromEntries(allCompanyKeys.map(companyKey => [companyKey, companyKey === key])),
                    }
                  : vendor,
              ),
            }
          : category,
      ),
    );
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setWarningDialog(prev => ({
      ...prev,
      open: false,
    }));
  };

  // 센터 경고 메시지 닫기 핸들러
  const handleCloseCenterWarning = () => {
    setCenterWarning({
      open: false,
      message: '',
    });
  };

  // 다이얼로그 확인 핸들러
  const handleConfirmDialog = () => {
    // 확인 콜백이 있으면 실행
    if (warningDialog.onConfirm) {
      warningDialog.onConfirm();
    }

    // 다이얼로그 닫기
    handleCloseDialog();
  };

  // 권한 업데이트 핸들러
  const handleUpdatePermission = (categoryIndex, vendorIndex) => {
    // 저장 전 경고 다이얼로그 표시
    setWarningDialog({
      open: true,
      title: '주의',
      message: '정말 스위칭 하시겠습니까 ?',
      onConfirm: () => performUpdatePermission(categoryIndex, vendorIndex),
    });
  };

  // 실제 권한 업데이트 실행
  const performUpdatePermission = async (categoryIndex, vendorIndex) => {
    setLoading(true);
    clearMessages();

    const category = categories[categoryIndex];
    const vendor = category.vendors[vendorIndex];

    try {
      // 선택된 게임 회사 결정 (활성화된 하나만 선택)
      let selectedCompany = 'axegames'; // 기본값

      if (vendor.thenuts) selectedCompany = 'thenuts';
      else if (vendor.honorlink) selectedCompany = 'honorlink';
      else if (vendor.rave) selectedCompany = 'rave';
      else if (vendor.hive_star) selectedCompany = 'hive_star';
      else if (vendor.the_a) selectedCompany = 'the_a';
      else if (vendor.devaplay) selectedCompany = 'devaplay';
      else if (vendor.next_gate) selectedCompany = 'next_gate'; // next_gate 추가

      // 선택된 에이전트 ID 사용
      const agentId = selectedAgentId;
      
      if (!agentId) {
        const user = getUser();
        if (!user || !user.id) {
          setError('로그인 정보를 찾을 수 없습니다.');
          setLoading(false);
          return;
        }
      }

      // API 호출하여 권한 업데이트
      const response = await axiosInstance.post('/games/agent/company/update', {
        agent_id: agentId,
        provider: vendor.name,
        category: category.name === '카지노' ? 'casino' : 'slot',
        game_company: selectedCompany,
        use: vendor.use ? 1 : 0,
      });

      if (!isMounted.current) return;

      if (response.data && response.data.success) {
        // 성공 메시지 대신 중앙 경고 메시지 표시
        if (response.data.result === false) {
          setCenterWarning({
            open: true,
            message: '이 설정은 변경할 수 없습니다. 먼저 리셋 버튼으로 기본 설정으로 돌린 후 변경해주세요.',
          });
        } else {
          setSuccess(response.data.message || '게임사 권한이 성공적으로 업데이트되었습니다.');
        }

        setError(null);

        // 데이터 다시 로드
        if (response.data.message && response.data.message.includes('기본 설정')) {
          fetchVendors(category.name === '카지노' ? 'casino' : 'slot');
        }
      } else {
        throw new Error(response.data.message || '권한 업데이트에 실패했습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;

      console.error('권한 업데이트 실패:', err);
      // 중앙 경고 메시지로 표시
      setCenterWarning({
        open: true,
        message: '이 설정은 변경할 수 없습니다. 먼저 리셋 버튼으로 기본 설정으로 돌린 후 변경해주세요.',
      });
      setSuccess(null);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 설정 리셋 핸들러
  const handleResetSettings = (categoryIndex, vendorIndex) => {
    // 리셋 전 경고 다이얼로그 표시
    setWarningDialog({
      open: true,
      title: '주의',
      message: '개별 게임사의 베팅제한금 당첨제한금과 개별 게임사 점검은 리셋됩니다. 다시 설정해주세요.',
      onConfirm: () => performResetSettings(categoryIndex, vendorIndex),
    });
  };

  // 실제 리셋 실행
  const performResetSettings = async (categoryIndex, vendorIndex) => {
    setLoading(true);
    clearMessages();

    const category = categories[categoryIndex];
    const vendor = category.vendors[vendorIndex];

    try {
      // 선택된 에이전트 ID 사용
      const agentId = selectedAgentId;
      
      if (!agentId) {
        const user = getUser();
        if (!user || !user.id) {
          setError('로그인 정보를 찾을 수 없습니다.');
          setLoading(false);
          return;
        }
      }

      // API 호출하여 설정 리셋
      const response = await axiosInstance.post('/games/agent/company/reset', {
        agent_id: agentId,
        provider: vendor.name,
        category: category.name === '카지노' ? 'casino' : 'slot',
      });

      if (!isMounted.current) return;

      if (response.data && response.data.success) {
        setSuccess(response.data.message || '게임사 설정이 기본값으로 리셋되었습니다.');
        setError(null);

        // 데이터 다시 로드
        fetchVendors(category.name === '카지노' ? 'casino' : 'slot');
      } else {
        throw new Error(response.data.message || '설정 리셋에 실패했습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;

      console.error('설정 리셋 실패:', err);
      setError(err.response?.data?.message || err.message || '알 수 없는 오류가 발생했습니다.');
      setSuccess(null);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 에이전트 선택 변경 핸들러
  const handleAgentChange = (event) => {
    setSelectedAgentId(event.target.value);
  };

  // 사용자 정의 중앙 경고 컴포넌트
  const CenteredWarning = () => (
    <Dialog
      open={centerWarning.open}
      onClose={handleCloseCenterWarning}
      PaperProps={{
        sx: darkModeStyles.dialogPaper
      }}
    >
      <Box sx={darkModeStyles.warningBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ErrorOutlineIcon sx={{ mr: 1, fontSize: 28, color: '#f87171' }} />
          <Typography variant="h6" sx={{ color: '#f87171', fontWeight: 'bold' }}>
            주의
          </Typography>
        </Box>

        <Typography sx={{ mb: 2, color: '#f87171' }}>{centerWarning.message}</Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            onClick={handleCloseCenterWarning} 
            variant="contained" 
            sx={darkModeStyles.saveButton}
          >
            확인
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

  // 특정 벤더의 사용 가능한 API 이름(게임회사) 목록 가져오기
  const getAvailableCompanies = (providerName) => {
    // 해당 벤더의 API 이름 목록 가져오기
    const apiNames = vendorApiNames[providerName] || [];
    
    // API 이름에 해당하는 회사만 필터링
    return Object.entries(companyMapping).filter(([key]) => apiNames.includes(key));
  };

  return (
    <Box sx={darkModeStyles.container}>
      {/* 중앙 경고 메시지 */}
      <CenteredWarning />

      <Paper sx={darkModeStyles.mainPaper}>
        <Typography variant="h4" sx={darkModeStyles.title} gutterBottom>
          게임사 벤더 설정
        </Typography>

        {/* 에이전트 선택 드롭다운 추가 */}
        <FormControl fullWidth sx={darkModeStyles.formControl}>
          <InputLabel>에이전트 아이디</InputLabel>
          <Select
            value={selectedAgentId}
            onChange={handleAgentChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'rgba(35, 35, 50, 0.95)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                },
              },
            }}
          >
            {agentList.map(agent => (
              <MenuItem 
                key={agent.id} 
                value={agent.id} 
                sx={agent.level === 0 ? darkModeStyles.menuItemHighlight : darkModeStyles.menuItem}
              >
                {agent.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 카테고리 탭 */}
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="게임 카테고리" 
            sx={darkModeStyles.tabs}
          >
            <Tab label="카지노" />
            <Tab label="슬롯" />
          </Tabs>
        </Box>

        {/* 알림 메시지 표시 */}
        {error && (
          <Alert severity="error" sx={darkModeStyles.alertError}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={darkModeStyles.alertSuccess}>
            {success}
          </Alert>
        )}

        {/* 로딩 표시 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: '#60a5fa' }} />
          </Box>
        )}

        {/* 현재 선택된 카테고리만 표시 */}
        <Grid container spacing={3}>
          {categories[activeTab] && (
            <Grid item xs={12}>
              <Typography variant="h5" sx={darkModeStyles.categoryTitle}>
                {categories[activeTab].name}
              </Typography>
              <Grid container spacing={3}>
                {categories[activeTab].vendors.length > 0 ? (
                  categories[activeTab].vendors.map((vendor, vendorIndex) => (
                    <Grid item xs={12} key={vendor.name}>
                      <Paper
                        elevation={3}
                        sx={darkModeStyles.vendorCard}
                      >
                        {/* Left Section: Vendor Name */}
                        <Box sx={darkModeStyles.vendorImage}>
                          <Typography variant="h6" sx={darkModeStyles.vendorName}>
                            {vendor.name}
                          </Typography>
                        </Box>

                        {/* Right Section: Content */}
                        <Box
                          sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                            flexWrap: 'wrap',
                          }}
                        >
                          {/* Switch List - 필터링된 게임회사만 표시 */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexWrap: 'wrap',
                              gap: 6,
                              flex: 1,
                            }}
                          >
                            {getAvailableCompanies(vendor.name).map(([key, displayName]) => (
                              <Box
                                key={key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={darkModeStyles.companyText}
                                >
                                  {displayName}
                                </Typography>
                                <Switch 
                                  checked={vendor[key]} 
                                  onChange={() => handleSwitchChange(activeTab, vendorIndex, key)} 
                                  sx={darkModeStyles.switchStyles}
                                />
                              </Box>
                            ))}
                            
                            {/* API 이름 로딩 중 또는 없을 경우 메시지 표시 */}
                            {(!vendorApiNames[vendor.name] || vendorApiNames[vendor.name].length === 0) && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.5)',
                                  fontStyle: 'italic' 
                                }}
                              >
                                {vendorApiNames[vendor.name] === undefined 
                                  ? '게임회사 정보 로딩 중...' 
                                  : '사용 가능한 게임회사가 없습니다.'}
                              </Typography>
                            )}
                          </Box>

                          {/* Buttons */}
                          <Stack direction="row" spacing={2}>
                            {/* Reset Button */}
                            {/* <Button
                              variant="outlined"
                              color="secondary"
                              size="medium"
                              disabled={loading}
                              startIcon={<SettingsBackupRestoreIcon sx={{ color: '#fff' }} />}
                              sx={darkModeStyles.resetButton}
                              onClick={() => handleResetSettings(activeTab, vendorIndex)}
                            >
                              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : '기본 설정으로 리셋'}
                            </Button> */}

                            {/* Update Button */}
                            <Button
                              variant="contained"
                              color="primary"
                              size="medium"
                              disabled={loading}
                              startIcon={<SaveIcon />}
                              sx={darkModeStyles.saveButton}
                              onClick={() => handleUpdatePermission(activeTab, vendorIndex)}
                            >
                              {loading ? <CircularProgress size={24} color="inherit" /> : '권한 저장'}
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: 100, 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontStyle: 'italic'
                    }}>
                      <Typography>데이터가 없습니다.</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* 경고 다이얼로그 */}
      <Dialog 
        open={warningDialog.open} 
        onClose={handleCloseDialog} 
        aria-labelledby="alert-dialog-title" 
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: darkModeStyles.dialogPaper
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={darkModeStyles.warningDialogTitle}>
          {warningDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={darkModeStyles.warningDialogText}>
            {warningDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={darkModeStyles.dialogButton}>
            취소
          </Button>
          <Button 
            onClick={handleConfirmDialog} 
            variant="contained" 
            sx={darkModeStyles.saveButton} 
            autoFocus
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GameVendor;